/**
 * lib/featured-sounds.ts — pure selection logic for the "featured / trending"
 * rail, shared by the server data layer (soundsApi.getFeaturedSounds) and the
 * client TrendingSection (which now fetches via /api/sounds and applies the
 * same selection client-side, per Stage 4b).
 *
 * Given a downloads-sorted pool, pick `limit` sounds favoring category
 * diversity first, then filling remaining slots with the next most-downloaded.
 * Keeping this in one tested place guarantees the homepage trending rail looks
 * identical whether it was server-rendered or hydrated from the public API.
 */

import { type Sound } from '@/lib/supabase-types'

/**
 * Pick up to `limit` sounds from `pool` (assumed already sorted by the desired
 * primary order, e.g. downloads desc), preferring one-per-category first then
 * topping up with the remaining highest-ranked sounds. Order within the result
 * follows the input order. Pure — no mutation of `pool`.
 */
export function pickDiverseSounds(pool: Sound[], limit: number): Sound[] {
  if (limit <= 0) return []

  const seenCategories = new Set<string>()
  const pickedIds = new Set<string>()
  const picked: Sound[] = []

  // First pass: one sound per not-yet-seen category, in rank order.
  for (const sound of pool) {
    if (picked.length >= limit) break
    if (!seenCategories.has(sound.category)) {
      picked.push(sound)
      seenCategories.add(sound.category)
      pickedIds.add(sound.id)
    }
  }

  // Second pass: fill remaining slots with the next highest-ranked sounds.
  if (picked.length < limit) {
    for (const sound of pool) {
      if (picked.length >= limit) break
      if (!pickedIds.has(sound.id)) {
        picked.push(sound)
        pickedIds.add(sound.id)
      }
    }
  }

  return picked
}
